import type { AuditAction } from '@/api/generated';

/**
 * What each audit code means, in words.
 *
 * Typed against the backend's published enum, so a code added there fails the
 * build here until it has a phrase. Written in the past tense, because every
 * entry is something that has already happened.
 */
export const ACTION_PHRASES: Record<AuditAction, string> = {
  'organization.created': 'Created the organization',
  'organization.updated': 'Updated the organization’s account',
  'organization.status_changed': 'Changed the organization’s status',
  'organization.profile_updated': 'Updated the profile',
  'organization.logo_updated': 'Changed the logo',
  'organization.cover_updated': 'Changed the cover image',
  'organization.billing_contact_updated': 'Changed the billing contact',
  'invitation.sent': 'Sent an invitation',
  'invitation.resent': 'Resent an invitation',
  'invitation.revoked': 'Revoked an invitation',
  'invitation.accepted': 'Accepted an invitation',
  'user.role_changed': 'Changed a user’s role',
  'user.removed': 'Removed a user',
};

/** Every code, in the order the backend publishes them, for the filter. */
export const ACTION_CODES = Object.keys(ACTION_PHRASES) as AuditAction[];

/**
 * The phrase for a code, or the code itself.
 *
 * An entry's `action` is a plain string: a backend deployed ahead of the portal
 * can send a code the portal has never met. Showing it as it came is honest; a
 * blank or a guess would not be.
 */
export function actionPhrase(code: string): string {
  return (ACTION_PHRASES as Record<string, string>)[code] ?? code;
}
